import React from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { Plus, Users, ArrowLeft, AlertTriangle } from 'lucide-react';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

// Mock data for team members
const mockTeamMembers = [
  {
    id: '1',
    name: 'Alex Johnson',
    email: 'alex.johnson@example.com',
    role: 'admin' as const,
    avatar: 'https://i.pravatar.cc/150?u=alex',
  },
  {
    id: '2',
    name: 'Maria Garcia',
    email: 'maria.garcia@example.com',
    role: 'member' as const,
    avatar: 'https://i.pravatar.cc/150?u=maria',
  },
  {
    id: '3',
    name: 'Sam Taylor',
    email: 'sam.taylor@example.com',
    role: 'member' as const,
    avatar: 'https://i.pravatar.cc/150?u=sam',
  },
  {
    id: '4',
    name: 'Jordan Lee',
    email: 'jordan.lee@example.com',
    role: 'member' as const,
    avatar: 'https://i.pravatar.cc/150?u=jordan',
  },
];

export default function ProjectTeam() {
  const { user } = useAuth();
  const [projectName, setProjectName] = React.useState<string>('');
  
  // Get the selected project from localStorage
  React.useEffect(() => {
    const storedProjectName = localStorage.getItem('selectedProject');
    if (storedProjectName) {
      setProjectName(storedProjectName);
    }
  }, []);

  // In a real application, we would fetch the team members for the current project
  // For now, use mock data
  const { data: teamMembers, isLoading, error } = useQuery({
    queryKey: ['teamMembers', 'project'],
    queryFn: async () => {
      // Simulating API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In a real application, we would fetch team members from the API
      // For now, return mock data
      return mockTeamMembers;
    },
    enabled: !!user
  });

  const handleInviteMember = () => {
    toast({
      title: "Invite sent",
      description: "Team member has been invited to the project.",
    });
  };

  const getRoleBadgeVariant = (role: 'admin' | 'member') => {
    switch (role) {
      case 'admin':
        return 'default';
      case 'member':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  return (
    <DashboardLayout>
      <motion.div
        className="space-y-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center space-x-4 mb-6">
          <Button variant="outline" size="icon" asChild>
            <Link to="/dashboard">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-semibold">
              {projectName || 'Project'} Team
            </h1>
            <p className="text-muted-foreground">Manage project team members</p>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <motion.h2 
            className="text-xl font-medium flex items-center gap-2"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Users className="h-5 w-5" />
            Team Members
          </motion.h2>
          
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Button onClick={handleInviteMember}>
              <Plus className="mr-2 h-4 w-4" />
              Invite Member
            </Button>
          </motion.div>
        </div>
        
        {/* Team Members Table */}
        <motion.div 
          className="w-full"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {isLoading ? (
            // Skeleton loading
            <Card>
              <CardContent className="p-6">
                <div className="space-y-2">
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                </div>
              </CardContent>
            </Card>
          ) : error ? (
            // Error state
            <Card>
              <CardContent className="flex flex-col items-center justify-center p-8 text-center">
                <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" />
                <h3 className="text-lg font-medium">Error loading team members</h3>
                <p className="text-muted-foreground">Please try again later.</p>
              </CardContent>
            </Card>
          ) : teamMembers && teamMembers.length > 0 ? (
            // Display team members in a table
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Member</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {teamMembers.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={member.avatar} alt={member.name} />
                            <AvatarFallback>{member.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{member.name}</span>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{member.email}</TableCell>
                        <TableCell>
                          <Badge variant={getRoleBadgeVariant(member.role)}>
                            {member.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm">
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            // Empty state
            <Card>
              <CardContent className="flex flex-col items-center justify-center p-8 text-center">
                <Users className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No team members found</h3>
                <p className="text-muted-foreground">Invite team members to collaborate on this project.</p>
                <Button className="mt-4" onClick={handleInviteMember}>
                  <Plus className="mr-2 h-4 w-4" />
                  Invite Member
                </Button>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </motion.div>
    </DashboardLayout>
  );
}
